import { create } from 'zustand';
import { getUserPref, setUserPref, PREF_KEYS } from '../hooks/useUserPreferences';
import { resolveInitialUi2026, applyUi2026Class, hasSessionOverride } from '../theme/ui2026Flag';
import { applyAppFont, toAppFont, type AppFontKey } from '../theme/appFonts';

/**
 * UI size (density). Four since 2026-10-21 (Jeff): `medium` ("Default") got
 * tighter, the previous Default became `large` and the previous Large became
 * `xlarge`. The text sizes did not change; see theme/cssVariables.css.
 */
export type ThemeSize = 'small' | 'medium' | 'large' | 'xlarge';

export function isThemeSize(v: unknown): v is ThemeSize {
  return v === 'small' || v === 'medium' || v === 'large' || v === 'xlarge';
}

/** Where the size has been saved since there are four (global and per user). */
export const THEME_SIZE_KEY = 'app-theme-size-v2';
/** The three-size era's global key. Still written, for an older build (see legacyThemeSize). */
export const LEGACY_THEME_SIZE_KEY = 'app-theme-size';

/**
 * A size saved before there were four, as a size now: the old Large is
 * X-Large (same look as before); Compact and Default keep their names
 * (Default is the tighter one now, which is intended). Null for anything
 * else, including nothing saved (a user who never chose).
 *
 * Old and new both have a 'large', so the mapping only ever reads the OLD
 * keys, and only while the new key is empty: once a size is in the new key
 * it is never re-mapped, so a new 'large' (the old Default) stays Large.
 */
export function migrateLegacyThemeSize(v: unknown): ThemeSize | null {
  if (v === 'small' || v === 'medium') return v;
  if (v === 'large') return 'xlarge';
  return null;
}

/** The old-key value for a size chosen now, so an older build (a rollback) shows the nearest look. */
export function legacyThemeSize(size: ThemeSize): 'small' | 'medium' | 'large' {
  if (size === 'xlarge') return 'large';
  if (size === 'large') return 'medium';
  return size;
}

/**
 * The one set of names for the UI size options.
 *
 * The avatar menu and the User Settings page each had their own, and they
 * disagreed: the menu called `small` "Default" while the page called `medium`
 * "Default". `medium` is the actual default (see `loadThemeSize`), so a user
 * on a stock install saw "Large" highlighted in one place and "Default" in the
 * other, for the same setting.
 *
 * `xlarge` ("X-Large") is the old Large (formerly "Comfortable"); `large` is
 * the old Default.
 */
export const UI_SIZE_LABELS: Record<ThemeSize, string> = {
  small: 'Compact',
  medium: 'Default',
  large: 'Large',
  xlarge: 'X-Large',
};

/** Rendering order for the size picker, smallest first. */
export const UI_SIZE_ORDER: readonly ThemeSize[] = ['small', 'medium', 'large', 'xlarge'];

/**
 * Where the shared form fields (components/form) put their label.
 * - border: on the field's outline, in a notch (the DevExtreme / MUI outlined
 *   look PSI staff are used to). The default.
 * - above: above the field, outside the border.
 * - left: beside the field on the same row (compact forms); wraps to above at
 *   phone width.
 * A field's own `labelPlacement` prop overrides this. Checkboxes always keep
 * their text beside the box.
 */
export type LabelPlacement = 'border' | 'above' | 'left';

export const LABEL_PLACEMENT_LABELS: Record<LabelPlacement, string> = {
  border: 'On the border',
  above: 'Above',
  left: 'Beside',
};

/** Rendering order for the label placement picker. */
export const LABEL_PLACEMENT_ORDER: readonly LabelPlacement[] = ['border', 'above', 'left'];

export const DEFAULT_LABEL_PLACEMENT: LabelPlacement = 'border';

export function isLabelPlacement(v: unknown): v is LabelPlacement {
  return v === 'border' || v === 'above' || v === 'left';
}

interface ThemeState {
  darkMode: boolean;
  themeSize: ThemeSize;
  /** UI 2026 refresh. Off restores the pre-refresh look exactly. */
  ui2026: boolean;
  /**
   * High contrast accessibility theme. Layers on top of light or dark and
   * raises text to WCAG AAA (7:1) with heavier borders and a thicker focus
   * ring. Opt-in: the default palettes already meet AA.
   */
  highContrast: boolean;
  /** Where shared form fields put their label (see LabelPlacement). */
  labelPlacement: LabelPlacement;
  /** The app font (see theme/appFonts.ts). */
  font: AppFontKey;

  toggleDarkMode: () => void;
  setDarkMode: (dark: boolean) => void;
  setThemeSize: (size: ThemeSize) => void;
  toggleUi2026: () => void;
  setUi2026: (on: boolean) => void;
  toggleHighContrast: () => void;
  setHighContrast: (on: boolean) => void;
  setLabelPlacement: (placement: LabelPlacement) => void;
  setFont: (font: AppFontKey) => void;
  /** Called once after auth resolves the userId - loads user-scoped theme prefs */
  syncWithUser: (userId: number) => void;
}

// Global (non-user-scoped) keys used for initial load before auth resolves
function loadDarkMode(): boolean {
  try {
    return localStorage.getItem('app-dark-mode') === 'true';
  } catch {
    return false;
  }
}

function loadHighContrast(): boolean {
  try {
    return localStorage.getItem('app-high-contrast') === 'true';
  } catch {
    return false;
  }
}

function loadThemeSize(): ThemeSize {
  try {
    const saved = localStorage.getItem(THEME_SIZE_KEY);
    if (isThemeSize(saved)) return saved;
    // First load since there are four sizes: carry the old choice over once.
    const migrated = migrateLegacyThemeSize(localStorage.getItem(LEGACY_THEME_SIZE_KEY));
    if (migrated) {
      localStorage.setItem(THEME_SIZE_KEY, migrated);
      return migrated;
    }
  } catch {
    // ignore
  }
  return 'medium';
}

function loadLabelPlacement(): LabelPlacement {
  try {
    const saved = localStorage.getItem('app-label-placement');
    if (isLabelPlacement(saved)) return saved;
  } catch {
    // ignore
  }
  return DEFAULT_LABEL_PLACEMENT;
}

/** Global key for the app font, read before auth like the others. */
export const FONT_KEY = 'app-font';

function loadFont(): AppFontKey {
  try {
    return toAppFont(localStorage.getItem(FONT_KEY));
  } catch {
    return toAppFont(null);
  }
}

function applyFont(font: AppFontKey, userId?: number) {
  applyAppFont(font);
  try {
    localStorage.setItem(FONT_KEY, font);
  } catch {
    // ignore
  }
  if (userId && userId > 0) {
    setUserPref(userId, PREF_KEYS.FONT, font);
  }
}

function applyDarkMode(dark: boolean, userId?: number) {
  document.documentElement.classList.toggle('dark-mode', dark);
  document.body.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
  // Write to both global key (for initial load) and user-scoped key
  localStorage.setItem('app-dark-mode', String(dark));
  if (userId && userId > 0) {
    setUserPref(userId, PREF_KEYS.DARK_MODE, dark);
  }
}

function applyUi2026(on: boolean, userId?: number, persist = true) {
  applyUi2026Class(on, persist);
  if (persist && userId && userId > 0) {
    setUserPref(userId, PREF_KEYS.UI_2026, on);
  }
}

function applyHighContrast(on: boolean, userId?: number) {
  document.documentElement.classList.toggle('high-contrast', on);
  localStorage.setItem('app-high-contrast', String(on));
  if (userId && userId > 0) {
    setUserPref(userId, PREF_KEYS.HIGH_CONTRAST, on);
  }
}

function applyThemeSize(size: ThemeSize, userId?: number) {
  document.documentElement.setAttribute('data-theme-size', size);
  try {
    localStorage.setItem(THEME_SIZE_KEY, size);
    localStorage.setItem(LEGACY_THEME_SIZE_KEY, legacyThemeSize(size));
  } catch {
    // ignore
  }
  if (userId && userId > 0) {
    setUserPref(userId, PREF_KEYS.THEME_SIZE_V2, size);
    setUserPref(userId, PREF_KEYS.THEME_SIZE, legacyThemeSize(size));
  }
}

function applyLabelPlacement(placement: LabelPlacement, userId?: number) {
  document.documentElement.setAttribute('data-label-placement', placement);
  try {
    localStorage.setItem('app-label-placement', placement);
  } catch {
    // ignore
  }
  if (userId && userId > 0) {
    setUserPref(userId, PREF_KEYS.LABEL_PLACEMENT, placement);
  }
}

// Apply saved preferences immediately (global key - before auth)
const initialDark = loadDarkMode();
const initialSize = loadThemeSize();
const initialUi2026 = resolveInitialUi2026();
const initialHighContrast = loadHighContrast();
const initialLabelPlacement = loadLabelPlacement();
const initialFont = loadFont();
applyDarkMode(initialDark);
applyThemeSize(initialSize);
applyHighContrast(initialHighContrast);
applyLabelPlacement(initialLabelPlacement);
applyFont(initialFont);
// persist=false: booting with the build default must not write a sticky
// localStorage value the user never chose.
applyUi2026(initialUi2026, undefined, false);

// Track the current userId so set/toggle can write user-scoped prefs
let currentUserId = 0;

export const useThemeStore = create<ThemeState>((set) => ({
  darkMode: initialDark,
  themeSize: initialSize,
  ui2026: initialUi2026,
  highContrast: initialHighContrast,
  labelPlacement: initialLabelPlacement,
  font: initialFont,

  toggleDarkMode: () =>
    set((state) => {
      const next = !state.darkMode;
      applyDarkMode(next, currentUserId);
      return { darkMode: next };
    }),

  setDarkMode: (dark) => {
    applyDarkMode(dark, currentUserId);
    set({ darkMode: dark });
  },

  setThemeSize: (size) => {
    applyThemeSize(size, currentUserId);
    set({ themeSize: size });
  },

  toggleUi2026: () =>
    set((state) => {
      const next = !state.ui2026;
      applyUi2026(next, currentUserId);
      return { ui2026: next };
    }),

  setUi2026: (on) => {
    applyUi2026(on, currentUserId);
    set({ ui2026: on });
  },

  toggleHighContrast: () =>
    set((state) => {
      const next = !state.highContrast;
      applyHighContrast(next, currentUserId);
      return { highContrast: next };
    }),

  setHighContrast: (on) => {
    applyHighContrast(on, currentUserId);
    set({ highContrast: on });
  },

  setLabelPlacement: (placement) => {
    applyLabelPlacement(placement, currentUserId);
    set({ labelPlacement: placement });
  },

  setFont: (font) => {
    applyFont(font, currentUserId);
    set({ font });
  },

  syncWithUser: (userId: number) => {
    currentUserId = userId;
    if (userId <= 0) return;
    // Check if user has saved theme prefs - if so, apply them
    const userDark = getUserPref<boolean | null>(userId, PREF_KEYS.DARK_MODE, null);
    // The user's own size: the four-size key, or else their three-size choice
    // carried over once (old Large -> X-Large); applyThemeSize then writes the
    // new key, so it is never mapped again.
    const savedSize = getUserPref<unknown>(userId, PREF_KEYS.THEME_SIZE_V2, null);
    const userSize = isThemeSize(savedSize) ? savedSize : migrateLegacyThemeSize(getUserPref<unknown>(userId, PREF_KEYS.THEME_SIZE, null));
    const userUi2026 = getUserPref<boolean | null>(userId, PREF_KEYS.UI_2026, null);
    const userHighContrast = getUserPref<boolean | null>(userId, PREF_KEYS.HIGH_CONTRAST, null);
    const userLabelPlacement = getUserPref<LabelPlacement | null>(userId, PREF_KEYS.LABEL_PLACEMENT, null);
    const userFont = getUserPref<unknown>(userId, PREF_KEYS.FONT, null);

    const updates: Partial<ThemeState> = {};
    if (userDark !== null) {
      applyDarkMode(userDark, userId);
      updates.darkMode = userDark;
    }
    if (userSize !== null) {
      applyThemeSize(userSize, userId);
      updates.themeSize = userSize;
    }
    // A ?ui= param is an explicit per-session escape hatch and must outrank
    // the stored preference - otherwise a user whose pref turned the refresh
    // on could never use ?ui=classic to get out of it.
    if (userUi2026 !== null && !hasSessionOverride()) {
      applyUi2026(userUi2026, userId);
      updates.ui2026 = userUi2026;
    }
    if (userHighContrast !== null) {
      applyHighContrast(userHighContrast, userId);
      updates.highContrast = userHighContrast;
    }
    if (isLabelPlacement(userLabelPlacement)) {
      applyLabelPlacement(userLabelPlacement, userId);
      updates.labelPlacement = userLabelPlacement;
    }
    // A saved font no longer offered goes back to the default rather than
    // keeping whatever the last user on this machine had.
    if (userFont !== null) {
      const font = toAppFont(userFont);
      applyFont(font, userId);
      updates.font = font;
    }
    if (Object.keys(updates).length > 0) {
      set(updates);
    }
  },
}));
